import { supabase } from './supabase'
import { captureGeolocation, type GeoCaptureResult, type GeoPosition } from './geolocation'
import { haversineKm } from './routeOptimizer'
import { usesOpsDashboard } from './fieldHome'
import i18n from '../i18n'

/** Hard site radius in meters. GPS accuracy is stored but never added to this fence. */
export const BASE_GEOFENCE_M = 100

export type FieldGpsErrorCode =
  | 'gps_required'
  | 'geofence'
  | 'no_site_coords'
  | 'cancelled'

export class FieldGpsError extends Error {
  code: FieldGpsErrorCode
  geo: GeoCaptureResult | null
  distanceFt: number | null

  constructor(
    message: string,
    code: FieldGpsErrorCode,
    opts?: { geo?: GeoCaptureResult | null; distanceFt?: number | null }
  ) {
    super(message)
    this.name = 'FieldGpsError'
    this.code = code
    this.geo = opts?.geo ?? null
    this.distanceFt = opts?.distanceFt ?? null
    // CRA compiles to ES5; without this, `instanceof FieldGpsError` is false
    // and callers swallow the real GPS/geofence message.
    Object.setPrototypeOf(this, FieldGpsError.prototype)
  }
}

export function isFieldGpsError(error: unknown): error is FieldGpsError {
  if (error instanceof FieldGpsError) return true
  if (!error || typeof error !== 'object') return false
  const e = error as { name?: string; code?: string }
  return e.name === 'FieldGpsError' && typeof e.code === 'string'
}

export function metersToFeet(m: number): number {
  return m * 3.28084
}

/** Always 100 m. Accuracy is ignored so a coarse GPS fix cannot expand the fence. */
export function allowedRadiusM(_accuracyM?: number | null): number {
  return BASE_GEOFENCE_M
}

export function distanceMeters(
  a: { latitude: number; longitude: number },
  b: { latitude: number; longitude: number }
): number {
  return haversineKm(a.latitude, a.longitude, b.latitude, b.longitude) * 1000
}

function gpsRequiredMessage(error: GeoCaptureResult['error']): string {
  if (error === 'denied') {
    return i18n.t('field:gps.denied')
  }
  if (error === 'unsupported') {
    return i18n.t('field:gps.unsupported')
  }
  if (error === 'timeout') {
    return i18n.t('field:gps.timeout')
  }
  return i18n.t('field:gps.generic')
}

/** GPS is mandatory — does not complete without a fix. */
export async function requireGpsCapture(): Promise<GeoCaptureResult> {
  const geo = await captureGeolocation()
  if (!geo.position) {
    throw new FieldGpsError(gpsRequiredMessage(geo.error), 'gps_required', { geo })
  }
  return geo
}

export async function resolveCanOverrideGeofence(): Promise<boolean> {
  const {
    data: { user },
  } = await supabase.auth.getUser()
  if (!user) return false
  const { data: profile } = await supabase
    .from('user_profiles')
    .select('role, is_super_admin')
    .eq('id', user.id)
    .maybeSingle()
  return usesOpsDashboard(
    profile?.role,
    !!(profile as { is_super_admin?: boolean } | null)?.is_super_admin
  )
}

async function fetchJobSiteCoords(
  jobId: string
): Promise<{ latitude: number; longitude: number; label: string } | null> {
  const { data, error } = await supabase
    .from('jobs')
    .select(
      `
      id,
      property:properties(latitude, longitude, name, address)
    `
    )
    .eq('id', jobId)
    .maybeSingle()
  if (error || !data) return null
  const prop = data.property as
    | {
        latitude?: number | null
        longitude?: number | null
        name?: string | null
        address?: string | null
      }
    | null
    | undefined
  if (
    prop?.latitude == null ||
    prop?.longitude == null ||
    !Number.isFinite(Number(prop.latitude)) ||
    !Number.isFinite(Number(prop.longitude))
  ) {
    return null
  }
  return {
    latitude: Number(prop.latitude),
    longitude: Number(prop.longitude),
    label: prop.name || prop.address || 'job site',
  }
}

async function logGateFailure(params: {
  jobId?: string | null
  attemptType: string
  reason: string
  geo: GeoCaptureResult | null
  distanceFt?: number | null
}): Promise<void> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser()
    let employeeId: string | null = null
    if (user?.id) {
      try {
        const { data } = await supabase.rpc('get_employee_for_user', { p_user_id: user.id })
        employeeId = (data as string) || null
      } catch {
        employeeId = null
      }
    }
    await supabase.from('time_punch_failures').insert({
      employee_id: employeeId,
      user_id: user?.id || null,
      attempt_type: params.attemptType,
      job_id: params.jobId || null,
      reason: params.reason,
      latitude: params.geo?.position?.latitude ?? null,
      longitude: params.geo?.position?.longitude ?? null,
      distance_ft: params.distanceFt ?? null,
    })
  } catch {
    // non-blocking audit
  }
}

export type JobSiteGateResult = {
  geo: GeoCaptureResult
  position: GeoPosition
  distanceFt: number | null
  allowedFt: number | null
  geofenceOverridden: boolean
}

/**
 * Ops desktop status edits (Job Details / dispatch): stamp GPS when the
 * browser has a fix, but never block. Field Start/Complete from My Day
 * must keep using `gateJobSiteAction` (GPS required; ops may override fence).
 */
export async function captureOptionalJobSiteGps(
  jobId: string
): Promise<JobSiteGateResult | null> {
  const geo = await captureGeolocation()
  if (!geo.position) return null
  const position = geo.position
  const site = await fetchJobSiteCoords(jobId)
  if (!site) {
    return {
      geo,
      position,
      distanceFt: null,
      allowedFt: null,
      geofenceOverridden: true,
    }
  }
  const distM = distanceMeters(position, site)
  const allowM = allowedRadiusM(position.accuracy)
  return {
    geo,
    position,
    distanceFt: metersToFeet(distM),
    allowedFt: metersToFeet(allowM),
    geofenceOverridden: distM > allowM,
  }
}

/**
 * Require GPS for everyone. Enforce property geofence for job-site actions.
 * Admin / office / field_manager / crew_leader may override the fence (GPS still required).
 */
export async function gateJobSiteAction(params: {
  jobId: string
  actionLabel: string
  attemptType: 'job_start' | 'job_complete' | 'checklist_photo' | 'job_photo'
  canOverrideGeofence?: boolean
}): Promise<JobSiteGateResult> {
  const geo = await requireGpsCapture()
  const position = geo.position!
  const canOverride =
    params.canOverrideGeofence ?? (await resolveCanOverrideGeofence())

  const site = await fetchJobSiteCoords(params.jobId)
  if (!site) {
    await logGateFailure({
      jobId: params.jobId,
      attemptType: params.attemptType,
      reason: 'no_site_coords',
      geo,
    })
    if (canOverride) {
      const ok = window.confirm(
        i18n.t('field:gps.noSiteCoordsOverride', { action: params.actionLabel })
      )
      if (!ok) {
        throw new FieldGpsError(i18n.t('field:gps.cancelled'), 'cancelled', { geo })
      }
      return {
        geo,
        position,
        distanceFt: null,
        allowedFt: null,
        geofenceOverridden: true,
      }
    }
    throw new FieldGpsError(
      i18n.t('field:gps.noSiteCoordsBlocked', { action: params.actionLabel }),
      'no_site_coords',
      { geo }
    )
  }

  const distM = distanceMeters(position, site)
  const allowM = allowedRadiusM(position.accuracy)
  const distanceFt = metersToFeet(distM)
  const allowedFt = metersToFeet(allowM)

  if (distM > allowM) {
    await logGateFailure({
      jobId: params.jobId,
      attemptType: params.attemptType,
      reason: 'geofence',
      geo,
      distanceFt,
    })
    if (canOverride) {
      const ok = window.confirm(
        i18n.t('field:gps.geofenceOverride', {
          action: params.actionLabel,
          feet: Math.round(distanceFt),
          site: site.label,
          allowed: Math.round(allowedFt),
        })
      )
      if (!ok) {
        throw new FieldGpsError(i18n.t('field:gps.cancelled'), 'cancelled', {
          geo,
          distanceFt,
        })
      }
      return {
        geo,
        position,
        distanceFt,
        allowedFt,
        geofenceOverridden: true,
      }
    }
    throw new FieldGpsError(
      i18n.t('field:gps.geofenceBlocked', {
        action: params.actionLabel,
        feet: Math.round(distanceFt),
        allowed: Math.round(allowedFt),
      }),
      'geofence',
      { geo, distanceFt }
    )
  }

  return {
    geo,
    position,
    distanceFt,
    allowedFt,
    geofenceOverridden: false,
  }
}

/** Day punch in/out — GPS required, no site geofence. */
export async function gateGpsOnlyAction(actionLabel: string): Promise<GeoCaptureResult> {
  try {
    return await requireGpsCapture()
  } catch (err) {
    if (err instanceof FieldGpsError) {
      await logGateFailure({
        attemptType: 'day_clock',
        reason: err.code === 'gps_required' ? 'gps_denied' : err.code,
        geo: err.geo,
      })
      throw new FieldGpsError(
        i18n.t('field:gps.actionPrefix', { action: actionLabel, message: err.message }),
        err.code,
        { geo: err.geo }
      )
    }
    throw err
  }
}
